import {groupByUniqueKey} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React, {useMemo, useState} from 'react';

import {pluralize} from 'BenchlingUtil';
import {isBioentitySample, isContainerSample, SampleDetails} from 'constants/SampleConstants';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {
  SamplesByColumn,
  SamplesSource,
} from 'notebook/sample-propagation/sample-collection/SamplesSourceCollector';
import {SampleFilterType} from 'notebook/sample-propagation/SampleFilter';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {
  FillPlateStructuredTableItemsQuery,
  FillPlateStructuredTableItemsQueryVariables,
} from 'samples/plate-maps/fill-from-structured-table-modal/__generated/FillPlateStructuredTableItemsQuery';
import FillPlateFromStructuredTableDropdown from 'samples/plate-maps/fill-from-structured-table-modal/FillPlateFromStructuredTableDropdown';
import FillPlateFromStructuredTableResults from 'samples/plate-maps/fill-from-structured-table-modal/FillPlateFromStructuredTableResults';
import {
  AccessibleStructuredTableItemNode,
  FILL_PLATE_LIST_ITEM_BIOENTITY_FRAGMENT,
  FILL_PLATE_LIST_ITEM_CONTAINER_FRAGMENT,
  StructuredTableItemNode,
} from 'samples/plate-maps/fill-plate-modal/FillPlateFromModalUtils';
import {ProcedureTestOrderConfig} from 'samples/plate-maps/integrations/procedures/PlateMapTestOrderUtils';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {OptionData} from 'ui-library/menus/MenuConstants';
import Modal from 'ui-library/modals/Modal';
import {SelectItemsFromListView} from 'worklists/SelectItemsFromListView';

export function isNonContainableBioentityColumn(
  containableSchemaIds: Set<string>,
  maybeContainableInterfaceIds: Set<string>,
  samplesByColumn: SamplesByColumn
): boolean {
  // If this column definitely has a tag schema (e.g. is a schema lookup column rather than an
  // 'any bioentity' column and that schema is not containable, display but disable this
  // option
  return (
    samplesByColumn.sampleFilter.type === SampleFilterType.BIOENTITY &&
    samplesByColumn.sampleFilter.schemaId != null &&
    !containableSchemaIds.has(samplesByColumn.sampleFilter.schemaId) &&
    !maybeContainableInterfaceIds.has(samplesByColumn.sampleFilter.schemaId)
  );
}

export const FILL_PLATE_STRUCTURED_TABLE_ITEMS_QUERY = gql`
  query FillPlateStructuredTableItemsQuery($bioentityIds: [StrictString!]!, $containerIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    bioentities(ids: $bioentityIds) {
      ... on RegistryItemInterface {
        ...FillPlateListItemBioentityFragment
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    containers(ids: $containerIds) {
      ... on ContainerNode {
        ...FillPlateListItemContainerFragment
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${FILL_PLATE_LIST_ITEM_BIOENTITY_FRAGMENT}
  ${FILL_PLATE_LIST_ITEM_CONTAINER_FRAGMENT}
`;

export interface FillPlateFromStructuredTableModalProps {
  /** The set of all API IDs of all containable schemas on the tenant */
  containableSchemaIds: Set<string>;
  /** The set of all interface IDs that have at least one containable implementing schema */
  maybeContainableInterfaceIds: Set<string>;
  /** The API ID of the plate to be filled; null if filling a new plate */
  destinationPlateId: string | null;
  /** Tables that a user can (possibly) choose reagents from */
  sampleSources: Array<SamplesSource>;
  onRequestClose: () => void;
  onRequestSubmit: (selectedItems: Array<ExcludeInaccessible<StructuredTableItemNode>>) => void;
  /** Set of provenance uuids of all tables that have at least one column that could have usable reagents */
  usableSampleSourceIds: Set<string>;
  /** Procedure config to validate test orders within assay context */
  procedureTestOrderConfig: ProcedureTestOrderConfig | null;
  plateMapType: PlateMapType;
}

export default function FillPlateFromStructuredTableModal({
  containableSchemaIds,
  maybeContainableInterfaceIds,
  destinationPlateId,
  sampleSources,
  onRequestClose,
  onRequestSubmit,
  usableSampleSourceIds,
  procedureTestOrderConfig,
  plateMapType,
}: FillPlateFromStructuredTableModalProps): JSX.Element {
  const [selectedItems, setSelectedItems] = useState<Array<AccessibleStructuredTableItemNode>>([]);

  const tablesByProvenanceUuid = useMemo(
    () => groupByUniqueKey(sampleSources, (samplesSource) => samplesSource.table!.provenanceUuid),
    [sampleSources]
  );
  const columnsByProvenanceUuid = useMemo(
    () =>
      groupByUniqueKey(
        sampleSources.flatMap((samplesSource) => samplesSource.samplesByColumn),
        (samplesByColumn) => samplesByColumn.column!.provenanceUuid
      ),
    [sampleSources]
  );

  const tableOptionDatas: Array<OptionData> = useMemo(
    () =>
      sampleSources.map((samplesSource) => {
        const shouldDisable = !usableSampleSourceIds.has(samplesSource.table!.provenanceUuid);
        return {
          disabled: shouldDisable,
          name: samplesSource.name,
          tooltipTitle: shouldDisable
            ? 'This table does not have entities that can be used in the plate'
            : null,
          value: samplesSource.table!.provenanceUuid,
        };
      }),
    [sampleSources, usableSampleSourceIds]
  );

  // Default to the first non-disabled table...
  const [selectedTableId, setSelectedTableId] = useState<string>(
    tableOptionDatas.find((tableOptionData) => !tableOptionData.disabled)!.value
  );

  const columnOptionDatas: Array<OptionData> = tablesByProvenanceUuid[selectedTableId].samplesByColumn.map(
    (samplesByColumn) => {
      const shouldDisable = isNonContainableBioentityColumn(
        containableSchemaIds,
        maybeContainableInterfaceIds,
        samplesByColumn
      );
      return {
        disabled: shouldDisable,
        name: samplesByColumn.displayName,
        tooltipTitle: shouldDisable
          ? 'This column does not have entities that can be used in the plate'
          : null,
        value: samplesByColumn.column!.provenanceUuid,
      };
    }
  );

  // ... and that table's first non-disabled column. The enabled/disabled status of tables is determined by
  // whether they have at least one non-disabled column, so this should find something.
  const [selectedColumnId, setSelectedColumnId] = useState<string>(
    columnOptionDatas.find((columnOptionData) => !columnOptionData.disabled)!.value
  );

  const searchInput = (
    <div className={css(styles.wrapper)}>
      <FillPlateFromStructuredTableDropdown
        disabled={sampleSources.length === 1}
        displayName={tablesByProvenanceUuid[selectedTableId].name}
        idSuffix="source-table"
        label="Source table"
        onSelect={(selectedValue) => {
          setSelectedTableId(selectedValue);
          setSelectedColumnId(
            tablesByProvenanceUuid[selectedValue].samplesByColumn[0].column!.provenanceUuid
          );
          setSelectedItems([]);
        }}
        optionDatas={tableOptionDatas}
        selectedValue={selectedTableId}
      />
      <FillPlateFromStructuredTableDropdown
        disabled={tablesByProvenanceUuid[selectedTableId].samplesByColumn.length === 1}
        displayName={columnsByProvenanceUuid[selectedColumnId].displayName}
        idSuffix="source-column"
        label="Source column"
        onSelect={(selectedValue: string) => {
          setSelectedColumnId(selectedValue);
          setSelectedItems([]);
        }}
        optionDatas={columnOptionDatas}
        selectedValue={selectedColumnId}
      />
    </div>
  );

  return (
    <Modal
      buttons={[
        {text: 'Cancel', cancel: true},
        {
          text: `Add ${selectedItems.length} ${pluralize(selectedItems.length, 'item')}`,
          style: ButtonStyle.PRIMARY,
          submit: true,
          disabled: selectedItems.length === 0,
        },
      ]}
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        onRequestSubmit(selectedItems);
      }}
      title="Add from structured table"
      expandHeight={true}
    >
      <SelectItemsFromListView<
        FillPlateStructuredTableItemsQuery,
        Array<SampleDetails>,
        FillPlateStructuredTableItemsQueryVariables
      >
        fullWidthSearchInput={true}
        listSearchInput={searchInput}
        listSummary={columnsByProvenanceUuid[selectedColumnId].samples}
        query={FILL_PLATE_STRUCTURED_TABLE_ITEMS_QUERY}
        queryVariablesFromListSummary={(samples: Array<SampleDetails>) => {
          const bioentityIds: Array<string> = [];
          const containerIds: Array<string> = [];
          for (const sample of samples) {
            if (isBioentitySample(sample)) {
              bioentityIds.push(sample.bioentityInfo.id);
            } else if (isContainerSample(sample)) {
              containerIds.push(sample.containerInfo.id);
            }
          }
          return {bioentityIds, containerIds};
        }}
      >
        {(result) => (
          <FillPlateFromStructuredTableResults
            destinationPlateId={destinationPlateId}
            result={result}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            procedureTestOrderConfig={procedureTestOrderConfig}
            plateMapType={plateMapType}
          />
        )}
      </SelectItemsFromListView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: '24px',
    justifyItems: 'stretch',
  },
  dropdownToggle: {
    flex: '1 1 auto',
  },
  listWrapper: {
    marginTop: '16px',
  },
});
